import { useMemo, memo } from "react";
import styled from "styled-components";
import Loading from "common/Loading";
import { NVLineChartDownsampled } from "common/graphs/DownSampling";
import { parseStatsLatencyData } from "views/Statistics/System/StatsLatency/api";
import * as d3 from "d3";
import { PERIOD_DASHBOARD_TO_HOURS, PERIOD_DASHBOARD_PERIOD_MAP } from "./utils";
import useDashboardData from "./useDashboardData";
import { LINES_BY_HOUR } from "common/constants";
import { atBqnTarget } from "common/api";

const metricLabelMap = {
  "Rtt-80%-Dn-Ms": "P80-80th percentile",
  "Rtt-Med-Dn-Ms": "P50-Median",
};

const metricTooltipMap = {
  "Rtt-80%-Dn-Ms":
    "The 80-th percentile of all downlink RTT latency, measured from BQN to end user, in 5 minutes.",
  "Rtt-Med-Dn-Ms":
    "The 50-th percentile (median) of all downlink RTT latency samples, measured from BQN to end user, in 5 minutes.",
 };

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 180px;
  color: var(--text-primary);
`;

const getLatencyData = ({ hours, mode, bqnId, period, range }) => {
  const periodLatency = PERIOD_DASHBOARD_PERIOD_MAP[period];
  const { from, to } = range;
  return ifCl
    .run(
      atBqnTarget(
        `show statistics latency-percentile lines ${hours * LINES_BY_HOUR}`,
        mode,
        bqnId,
      ),
    )
    .then((response) => ({
      data: parseStatsLatencyData(response, periodLatency, from, to),
      range,
    }));
};

function Latency({ period, range }) {
  // The range of the dashboard is the clock of the chart, which is asked for
  // again with each new one of them.
  const { data: dataRange, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId, range }) =>
      getLatencyData({
        hours: PERIOD_DASHBOARD_TO_HOURS[period],
        mode,
        bqnId,
        period,
        range,
      }),
    onError: "Error fetching latency data:",
    deps: [period],
    range,
  });

  // The fields are what the chart compares to decide whether it has to be
  // drawn again, so they are given the same identity while they are the same.
  const { data, range: rangeDrawn } = dataRange || {};
  const { items, fields } = data || {};
  const newFields = useMemo(
    () =>
      (fields ?? [])
        .filter((field) => field !== "Rtt-Min-Dn-Ms")
        .map((field) => ({
          name: field,
          label: metricLabelMap[field],
          tooltip: metricTooltipMap[field],
        })),
    [fields],
  );

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!dataRange || !dataRange.data || !dataRange.data.items || dataRange.data.items.length === 0) {
    return (
      <WrapperLoader>
        No data available
      </WrapperLoader>
    );
  }

  return (
    <Wrapper>
      <NVLineChartDownsampled
        xField="time"
        items={items}
        range={rangeDrawn}
        yAxisUnits={"milliseconds"}
        yAxisFormat={d3.format(".1f")}
        xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
        fields={newFields}
        yAxisTopGap={0.05}
        yAxisTicksNumber={4}
        xAxisTicksNumber={4}
        yTooltipFormat={(d) => d3.format(".2f")(d) + " milliseconds"}
        fixedYRange={[0, 1]}
        margin={{ top: 5, right: 10, bottom: 20, left: 60 }}
      />
    </Wrapper>
  );
}

export default memo(Latency);
